import {Trans} from '@lingui/macro';
import _, {compact, isUndefined, noop, without} from 'lodash';
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';

import {
  FrontClickEvent,
  Handler,
  HandlerOf,
  PredicateOf,
  RendererOf,
} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Dictionary} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {arrayToggle} from '../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupDivider} from '../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupDivider';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdown} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {SimpleDropdownItemWithColor} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItemWithColor';
import {PopoverTransferProps} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {useToggle} from '../../../srcCommon/helpers/hookHelpers';
import {buildIndexMap} from '../../../srcCommon/helpers/itemIndexHelpers';
import {compareName} from '../../../srcCommon/helpers/localeHelpers';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {ScrollDirectionsEnum} from '../../../srcCommon/scroll/scrollerTypes';
import {defaultAnswerOrFolderComparator} from '../../components/common/dropdowns/answerFolders/hierarchicalFolderSort';
import {
  VirtualTableColumnCellsEnum,
  virtualTableRowCheckboxWrapperWidth,
  virtualTableRowOffset,
  VirtualTableSortableColumnDirectionsEnum,
} from '../../components/common/table/helpers/tableHelpers';
import {StaticVirtualTable} from '../../components/common/table/staticVirtualTable';
import {
  SelectedColumnSort,
  SortableTableColumn,
  TableColumn,
} from '../../components/common/table/tableColumn';
import {TableEmptyState} from '../../components/common/table/tableEmptyState';
import {VirtualTableRowRenderer} from '../../components/common/table/virtualTableBase';
import {searchAnswersAndFolders} from '../../components/inboxes/composer/answers/answersSearchHelpers';
import {
  findAnswerOrFolderChildren,
  findAnswerOrFolderParents,
  makeKeyForAnswerOrFolder,
} from '../../helpers/models/answerHelpers';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {getSelectedIdsOnClick} from '../../helpers/selectionHelpers';
import {AnswerFolderModel, AnswerOrFolderModel, RAnswerFolder} from '../../models/api/answerFolderModel';
import {AnswerModel, RAnswer} from '../../models/api/answerModel';
import {SettingsAnswersUrl} from '../../models/url/settingsUrlModel';
import {
  AsyncMoreStatusesEnum,
  AsyncStatus,
  ResourceMap,
  ResourceTableStatusesEnum,
} from '../../store/appTypes';
import {SettingsContentHeader} from '../common/settingsContentHeader';
import {
  renderSettingsResourcesTableColumnName,
  SettingsResourcesTableColumnsEnum,
} from '../common/settingsResourcesCommon';
import {
  StyledSettingsContent,
  StyledSettingsMain,
  StyledSettingsSidebar,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {AddAnswerFolderDialogContainer} from './addAnswerFolderDialogContainer';
import {DeleteAnswerDialogContainer} from './deleteAnswerDialogContainer';
import {MoveAnswerDialogContainer} from './moveAnswerDialogContainer';
import {SettingsAnswersNavigationProps} from './settingsAnswers';
import {SettingsAnswersHeaderContainer} from './settingsAnswersHeaderContainer';
import {SettingsAnswersListSidebar} from './settingsAnswersListSidebar';
import {SettingsAnswersMultiSelectHeader} from './settingsAnswersMultiSelectHeader';
import {SettingsAnswerTableRow} from './settingsAnswerTableRow';

/*
 * Props.
 */

interface SettingsAnswersListProps extends SettingsAnswersNavigationProps {
  settingsAnswersUrl: SettingsAnswersUrl;
  infoBoxId: string;
  findShouldDisplayInfoBox: PredicateOf<string>;
  dismissSettingsInfoBox: HandlerOf<string>;
  answersAndFolders: ReadonlyArray<AnswerOrFolderModel>;
  loadingStatus: AsyncStatus;
  fetchAllAnswersAndFolders: Handler;
  folders: ResourceMap<AnswerFolderModel>;
  answers: ResourceMap<AnswerModel>;
  markAnswerFoldersAsStale: Handler;
  markAnswersAsStale: Handler;
}

/*
 * Component.
 */

export const SettingsAnswersList: FC<SettingsAnswersListProps> = (props) => {
  const theme = useUserTheme();
  const {
    settingsAnswersUrl,
    dismissSettingsInfoBox,
    infoBoxId,
    findShouldDisplayInfoBox,
    fetchAllAnswersAndFolders,
    loadingStatus,
    answersAndFolders,
    markAnswerFoldersAsStale,
    markAnswersAsStale,
  } = props;
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarOpen, toggleSidebar] = useToggle(
    !settingsAnswersUrl.folderId && !settingsAnswersUrl.answerId && findShouldDisplayInfoBox(infoBoxId),
  );
  const [idWithDropdownOpened, setIdWithDropdownOpened] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState<ReadonlyArray<string>>([]);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Dictionary<boolean>>({});
  const [sortedColumn, setSortedColumn] = useState<SelectedColumnSort | undefined>(undefined);
  const [isDeleteDialogOpened, setIsDeleteDialogOpened] = useState(false);
  const [isMoveDialogOpened, setIsMoveDialogOpened] = useState(false);
  const [addIntoFolderId, setAddIntoFolderId] = useState<number | undefined>(undefined);
  const [isAddFolderDialogOpened, setIsAddFolderDialogOpened] = useState(false);
  const [itemToActOn, setItemToActOn] = useState<AnswerOrFolderModel | undefined>(undefined);

  const isFolderExpanded = useCallback(
    (folderId: number) => {
      if (searchQuery) {
        return true;
      }

      return Boolean(expandedFolderIds[folderId]);
    },
    [expandedFolderIds, searchQuery],
  );

  const tableItemsComparator = useCallback(
    (a: AnswerOrFolderModel, b: AnswerOrFolderModel) => {
      if (!sortedColumn) {
        return defaultAnswerOrFolderComparator(a, b);
      }

      if (sortedColumn.id === SettingsResourcesTableColumnsEnum.NAME) {
        if (sortedColumn.direction === VirtualTableSortableColumnDirectionsEnum.DESCENDING) {
          if (RAnswerFolder.is(a) === RAnswerFolder.is(b)) {
            return compareName(a.name, b.name);
          }

          return RAnswerFolder.is(a) ? 1 : -1;
        }
        if (RAnswerFolder.is(a) === RAnswerFolder.is(b)) {
          return compareName(a.name, b.name);
        }

        return RAnswerFolder.is(a) ? -1 : 1;
      }

      if (sortedColumn.id === SettingsResourcesTableColumnsEnum.DATE_CREATED) {
        if (sortedColumn.direction === VirtualTableSortableColumnDirectionsEnum.DESCENDING) {
          return a.createdAt > b.createdAt ? -1 : 1;
        }

        return a.createdAt < b.createdAt ? -1 : 1;
      }

      return 0;
    },
    [sortedColumn],
  );

  const tableItems = useMemo(() => {
    if (!searchQuery) {
      return formatList(
        answersAndFolders,
        isFolderExpanded,
        settingsAnswersUrl.folderId,
        tableItemsComparator,
      );
    }

    return [...searchAnswersAndFolders(answersAndFolders, searchQuery)];
  }, [answersAndFolders, isFolderExpanded, searchQuery, settingsAnswersUrl.folderId, tableItemsComparator]);
  const totalOfItems = useMemo(() => {
    if (searchQuery) {
      return tableItems.length;
    }

    return answersAndFolders.length;
  }, [answersAndFolders.length, searchQuery, tableItems.length]);
  const shouldShowChevron = useMemo(() => {
    if (searchQuery) {
      return false;
    }

    return tableItems.some(
      (item) => RAnswerFolder.is(item) && (item.numChildAnswers > 0 || item.numChildFolders > 0),
    );
  }, [searchQuery, tableItems]);
  const itemIndexes = useMemo(
    () => buildIndexMap(tableItems.map((t) => makeKeyForAnswerOrFolder(t))),
    [tableItems],
  );
  const isItemSelected = useCallback(
    (item: AnswerOrFolderModel) => selectedItemIds.includes(makeKeyForAnswerOrFolder(item)),
    [selectedItemIds],
  );
  const toggleSelectedItem = useCallback(
    (item: AnswerOrFolderModel, e?: FrontClickEvent) => {
      const childrenIds = findAnswerOrFolderChildren(item, props.folders, props.answers).map((i) =>
        makeKeyForAnswerOrFolder(i),
      );
      const parentIds = findAnswerOrFolderParents(item, props.folders).map((i) =>
        makeKeyForAnswerOrFolder(i),
      );
      const itemId = makeKeyForAnswerOrFolder(item);

      setSelectedItemIds((prevSelected) => {
        function getInitialNewSelected() {
          if (!e || (!e?.shiftKey && !e?.metaKey)) {
            return arrayToggle(prevSelected, itemId);
          }

          if (prevSelected.length === 1 && itemId === prevSelected[0]) {
            return [];
          }

          return getSelectedIdsOnClick(
            prevSelected,
            tableItems.map((t) => makeKeyForAnswerOrFolder(t)),
            itemIndexes,
            itemId,
            e,
          );
        }

        let newSelected = getInitialNewSelected();

        // If we're inside a search, we don't want to potentially select the tree.
        if (searchQuery) {
          return newSelected;
        }

        const parentGotSelected = !prevSelected.includes(itemId) && newSelected.includes(itemId);
        const parentGotUnselected = prevSelected.includes(itemId) && !newSelected.includes(itemId);

        childrenIds.forEach((childId) => {
          if (
            (newSelected.includes(childId) && parentGotUnselected) ||
            (!newSelected.includes(childId) && parentGotSelected)
          ) {
            newSelected = arrayToggle(newSelected, childId);
          }
        });

        // If our tag has been deselected, we also need to deselect its parents
        if (!newSelected.includes(itemId)) {
          const parentsToDeselect = parentIds.filter((id) => newSelected.includes(id));
          newSelected = without(newSelected, ...parentsToDeselect);
        }

        return _(newSelected)
          .uniq()
          .sortBy((id) => itemIndexes[id])
          .value();
      });
    },
    [itemIndexes, props.answers, props.folders, searchQuery, tableItems],
  );

  const onItemClick = (event: FrontClickEvent, onNameCell: boolean, item: AnswerOrFolderModel) => {
    if (!onNameCell && selectedItemIds.length === 0) {
      return;
    }

    if (selectedItemIds.length > 0 || event.shiftKey || event.metaKey) {
      event.preventDefault();
      event.stopPropagation();
      toggleSelectedItem(item, event);
      return;
    }

    if (RAnswer.is(item)) {
      props.openAnswerEdit(item.id);
    } else {
      props.navigateToFolder(item.id);
    }
  };

  const onInfoBoxCloseClick = () => {
    toggleSidebar();
    dismissSettingsInfoBox(infoBoxId);
  };
  const onInfoBoxFooterClick = () => {
    openInBrowser('https://help.frontapp.com/t/q561cm/how-to-create-message-templates');
  };

  const renderRightHeaderButtons = () => {
    const {folderId, inFolder} = settingsAnswersUrl;
    return (
      <ButtonGroup>
        {inFolder && !isUndefined(folderId) && (
          <Button
            size={VisualSizesEnum.MEDIUM}
            variant={ButtonVariantsEnum.SECONDARY}
            onClick={() => props.openFolderEdit(folderId)}
          >
            <Trans id="settings-editfolder">Edit folder</Trans>
          </Button>
        )}

        <Button
          size={VisualSizesEnum.MEDIUM}
          variant={ButtonVariantsEnum.SECONDARY}
          onClick={() =>
            props.openNewFolder(settingsAnswersUrl.inFolder ? settingsAnswersUrl.folderId : undefined)
          }
        >
          <Trans id="settingsanswers-addfolder">Add a folder</Trans>
        </Button>
        <Button
          size={VisualSizesEnum.MEDIUM}
          variant={ButtonVariantsEnum.PRIMARY}
          onClick={() =>
            props.openNewAnswer(settingsAnswersUrl.inFolder ? settingsAnswersUrl.folderId : undefined)
          }
        >
          <Trans id="settingsanswers-addtemplate">Add a template</Trans>
        </Button>
        <ButtonGroupDivider />
        <IconButton
          iconName={isSidebarOpen ? 'infoFilled' : 'info'}
          label=""
          onClick={toggleSidebar}
          color={isSidebarOpen ? theme.palette.blue.shade40 : theme.greys.shade70}
          hoverColor={isSidebarOpen ? theme.palette.blue.shade40 : theme.greys.shade70}
        />
      </ButtonGroup>
    );
  };

  const resetState = useCallback(() => {
    markAnswersAsStale();
    markAnswerFoldersAsStale();
    setSelectedItemIds([]);
    setItemToActOn(undefined);
  }, [markAnswerFoldersAsStale, markAnswersAsStale]);

  useEffect(() => {
    if (loadingStatus.isStale) {
      fetchAllAnswersAndFolders();
    }
  }, [fetchAllAnswersAndFolders, loadingStatus.isStale]);

  const localize = useLocalize();

  const columns: ReadonlyArray<TableColumn<SettingsResourcesTableColumnsEnum>> = useMemo(
    () =>
      compact([
        {
          id: SettingsResourcesTableColumnsEnum.NAME,
          type: SettingsResourcesTableColumnsEnum.NAME,
          title: renderSettingsResourcesTableColumnName(SettingsResourcesTableColumnsEnum.NAME, localize),
          cellType: VirtualTableColumnCellsEnum.TEXT,
          settings: {
            sticky: {isSticky: true, stickyOffset: virtualTableRowCheckboxWrapperWidth},
            cellWidth: {
              shouldFillRemainingWidth: true,
              minWidth: 330 - virtualTableRowOffset,
            },
          },
        },
        isTeamNamespace(settingsAnswersUrl.namespace) && {
          id: SettingsResourcesTableColumnsEnum.AVAILABLE_IN,
          type: SettingsResourcesTableColumnsEnum.AVAILABLE_IN,
          title: renderSettingsResourcesTableColumnName(
            SettingsResourcesTableColumnsEnum.AVAILABLE_IN,
            localize,
          ),
          cellType: VirtualTableColumnCellsEnum.TEXT,
          settings: {
            cellWidth: {
              width: 140 - virtualTableRowOffset,
            },
          },
        },
        {
          id: SettingsResourcesTableColumnsEnum.DATE_CREATED,
          type: SettingsResourcesTableColumnsEnum.DATE_CREATED,
          title: renderSettingsResourcesTableColumnName(
            SettingsResourcesTableColumnsEnum.DATE_CREATED,
            localize,
          ),
          cellType: VirtualTableColumnCellsEnum.DATE,
          settings: {
            cellWidth: {
              width: 140 - virtualTableRowOffset,
            },
          },
        },
      ]),
    [localize, settingsAnswersUrl.namespace],
  );

  const renderActionsDropdown: RendererOf<{
    modalProps: PopoverTransferProps;
    value: AnswerOrFolderModel | undefined;
  }> = ({modalProps, value}) => {
    if (!value) {
      return null;
    }

    if (RAnswer.is(value)) {
      return (
        <SimpleDropdown anchor={modalProps.anchor} onRequestClose={modalProps.onRequestClose}>
          <SimpleDropdownItem
            onSelect={() => {
              props.openAnswerEdit(value?.id);
            }}
          >
            <Trans id="settingsanswers-edittemplate">Edit template</Trans>
          </SimpleDropdownItem>
          <SimpleDropdownItem
            onSelect={() => {
              setIsMoveDialogOpened(true);
              setItemToActOn(value);
            }}
          >
            <Trans id="settingsanswers-move">Move</Trans>
          </SimpleDropdownItem>
          <SimpleDropdownItemWithColor
            color={PaletteColorsEnum.RED}
            onSelect={() => {
              setIsDeleteDialogOpened(true);
              setItemToActOn(value);
            }}
          >
            <Trans id="settingsanswers-delete">Delete</Trans>
          </SimpleDropdownItemWithColor>
        </SimpleDropdown>
      );
    }

    return (
      <SimpleDropdown anchor={modalProps.anchor} onRequestClose={modalProps.onRequestClose}>
        <SimpleDropdownItem
          onSelect={() => {
            props.openFolderEdit(value?.id);
          }}
        >
          <Trans id="settingsanswers-editfolder">Edit folder</Trans>
        </SimpleDropdownItem>
        <SimpleDropdownItem
          onSelect={() => {
            props.openNewAnswer(value.id);
          }}
        >
          <Trans id="settingsanswers-addtemplate">Add a template</Trans>
        </SimpleDropdownItem>
        <SimpleDropdownItemWithColor
          color={PaletteColorsEnum.RED}
          onSelect={() => {
            setIsDeleteDialogOpened(true);
            setItemToActOn(value);
          }}
        >
          <Trans id="settingsanswers-delete">Delete</Trans>
        </SimpleDropdownItemWithColor>
      </SimpleDropdown>
    );
  };

  const renderRow: VirtualTableRowRenderer<AnswerOrFolderModel> = (item, options, siblingData) => (
    <SettingsAnswerTableRow
      settingsAnswersUrl={settingsAnswersUrl}
      answerOrFolder={item}
      hasChevron={shouldShowChevron}
      hasHover={makeKeyForAnswerOrFolder(item) === idWithDropdownOpened}
      isExpanded={RAnswerFolder.is(item) && isFolderExpanded(item.id)}
      isInSearch={Boolean(searchQuery)}
      isNextSiblingSelected={Boolean(siblingData?.next && isItemSelected(siblingData?.next))}
      isPreviousSiblingSelected={Boolean(siblingData?.previous && isItemSelected(siblingData?.previous))}
      isSelected={selectedItemIds.includes(makeKeyForAnswerOrFolder(item))}
      isInMultiSelectMode={selectedItemIds.length > 0}
      onCheckBoxToggle={(e) => toggleSelectedItem(item, e)}
      onNameCellClick={(e) => onItemClick(e, true, item)}
      onCellClick={(e) => onItemClick(e, false, item)}
      onActionsDropdownOpened={(isOpen) =>
        setIdWithDropdownOpened(isOpen ? makeKeyForAnswerOrFolder(item) : '')
      }
      onChevronClick={() => {
        if (RAnswer.is(item)) {
          return;
        }

        setExpandedFolderIds((prevExpanded) => ({
          ...prevExpanded,
          [item.id]: !prevExpanded[item.id],
        }));
      }}
      renderActionsDropdown={renderActionsDropdown}
      options={options}
      tableColumns={columns}
    />
  );

  const maybeRenderAddFolderDialog = () => {
    if (!isAddFolderDialogOpened) {
      return null;
    }

    const moveItems = itemToActOn
      ? [itemToActOn, ...findAnswerOrFolderChildren(itemToActOn, props.folders, props.answers)]
      : answersAndFolders.filter((i) => selectedItemIds.includes(makeKeyForAnswerOrFolder(i)));

    if (moveItems.length < 1) {
      return null;
    }

    return (
      <AddAnswerFolderDialogContainer
        onRequestClose={() => setIsAddFolderDialogOpened(false)}
        itemsToActOn={moveItems}
        namespace={settingsAnswersUrl.namespace}
        onCreate={() => {
          setIsAddFolderDialogOpened(false);
          resetState();
        }}
        prefilledFolderId={addIntoFolderId}
      />
    );
  };

  const maybeRenderMoveConfirmDialog = () => {
    if (!isMoveDialogOpened) {
      return null;
    }

    const moveItems = itemToActOn
      ? [itemToActOn, ...findAnswerOrFolderChildren(itemToActOn, props.folders, props.answers)]
      : answersAndFolders.filter((i) => selectedItemIds.includes(makeKeyForAnswerOrFolder(i)));

    if (moveItems.length < 1) {
      return null;
    }

    return (
      <MoveAnswerDialogContainer
        itemsToActOn={moveItems}
        settingsAnswersUrl={settingsAnswersUrl}
        onRequestClose={() => setIsMoveDialogOpened(false)}
        onMove={() => {
          setIsMoveDialogOpened(false);
          resetState();
        }}
        onAddFolderClick={(maybeFolderId) => {
          setIsMoveDialogOpened(false);
          setIsAddFolderDialogOpened(true);
          setAddIntoFolderId(maybeFolderId);
        }}
      />
    );
  };

  const maybeRenderDeleteConfirmDialog = () => {
    if (!isDeleteDialogOpened) {
      return null;
    }

    const deleteItems = itemToActOn
      ? [itemToActOn, ...findAnswerOrFolderChildren(itemToActOn, props.folders, props.answers)]
      : answersAndFolders.filter((i) => selectedItemIds.includes(makeKeyForAnswerOrFolder(i)));

    if (deleteItems.length < 1) {
      return null;
    }

    return (
      <DeleteAnswerDialogContainer
        itemsToActOn={deleteItems}
        answersUrl={settingsAnswersUrl}
        onRequestClose={() => setIsDeleteDialogOpened(false)}
        onDelete={() => {
          setIsDeleteDialogOpened(false);
          resetState();
        }}
      />
    );
  };

  const renderEmptyState = () => {
    // Total height of header components + margin for the empty state
    const headerOffset = !settingsAnswersUrl.inFolder ? 193 : 214;

    return (
      <TableEmptyState headerOffset={headerOffset}>
        <Trans id="settingsanswers-table-empty-state-description">You do not have any templates.</Trans>
      </TableEmptyState>
    );
  };

  return (
    <>
      {maybeRenderAddFolderDialog()}
      {maybeRenderMoveConfirmDialog()}
      {maybeRenderDeleteConfirmDialog()}
      <StyledSettingsWrapper>
        <SettingsAnswersHeaderContainer
          navigateToFolder={props.navigateToFolder}
          openRoot={props.openRoot}
          answersUrl={settingsAnswersUrl}
          right={renderRightHeaderButtons()}
        />
        <StyledSettingsMain>
          <StyledSettingsContent>
            <SettingsContentHeader
              searchInputValue={searchQuery}
              onSearchInput={setSearchQuery}
              onExitClick={() => setSearchQuery('')}
            />
            <StaticVirtualTable<AnswerOrFolderModel>
              items={tableItems}
              columns={columns}
              sortableColumns={settingsMessageTemplatesListSortableColumns}
              sortedColumn={sortedColumn}
              hasData={loadingStatus.status === ResourceTableStatusesEnum.LOADED}
              moreStatuses={{
                [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
                [ScrollDirectionsEnum.BOTTOM]: AsyncMoreStatusesEnum.ALL_LOADED,
              }}
              onLoadMore={noop}
              shouldRenderEmptyState={
                loadingStatus.status === ResourceTableStatusesEnum.LOADED && answersAndFolders.length === 0
              }
              renderEmpty={renderEmptyState}
              renderRow={renderRow}
              shouldAllowMultiSelect
              renderMultiSelectHeader={(onMultiSelectChange) => (
                <SettingsAnswersMultiSelectHeader
                  selectedIds={selectedItemIds}
                  totalItems={totalOfItems}
                  onDeleteClick={() => {
                    setIsDeleteDialogOpened(true);
                  }}
                  onMoveClick={() => {
                    setIsMoveDialogOpened(true);
                  }}
                  onMultiSelectChange={onMultiSelectChange}
                />
              )}
              isInMultiSelectMode={selectedItemIds.length > 0}
              onSortUpdate={setSortedColumn}
              onMultiSelectHeaderChange={(isSelectingAll) => {
                if (searchQuery) {
                  setSelectedItemIds(
                    isSelectingAll ? tableItems.map((i) => makeKeyForAnswerOrFolder(i)) : [],
                  );
                } else {
                  setSelectedItemIds(
                    isSelectingAll ? answersAndFolders.map((i) => makeKeyForAnswerOrFolder(i)) : [],
                  );
                }
              }}
            />
          </StyledSettingsContent>

          {isSidebarOpen && (
            <StyledSettingsSidebar>
              <SettingsAnswersListSidebar
                namespace={settingsAnswersUrl.namespace}
                onCloseClick={onInfoBoxCloseClick}
                onFooterClick={onInfoBoxFooterClick}
              />
            </StyledSettingsSidebar>
          )}
        </StyledSettingsMain>
      </StyledSettingsWrapper>
    </>
  );
};

const settingsMessageTemplatesListSortableColumns: ReadonlyArray<SortableTableColumn> = [
  SettingsResourcesTableColumnsEnum.NAME,
  SettingsResourcesTableColumnsEnum.DATE_CREATED,
].map((c) => ({
  id: c,
  directions: [
    VirtualTableSortableColumnDirectionsEnum.ASCENDING,
    VirtualTableSortableColumnDirectionsEnum.DESCENDING,
  ],
}));

function formatList(
  answersAndFolders: ReadonlyArray<AnswerOrFolderModel>,
  isFolderExpanded: PredicateOf<number>,
  parentFolderToIgnoreId = 0,
  sortItems = defaultAnswerOrFolderComparator,
): ReadonlyArray<AnswerOrFolderModel> {
  const parentFolderIdsToIgnore = new Set([parentFolderToIgnoreId]);
  const itemsByParent = _(answersAndFolders)
    .filter((item) => {
      if (RAnswer.is(item)) {
        return true;
      }

      if (parentFolderToIgnoreId) {
        return item.id !== parentFolderToIgnoreId && Boolean(item.parentFolderId);
      }

      return true;
    })
    .uniqBy((i) => makeKeyForAnswerOrFolder(i))
    .groupBy((item) => {
      const parentFolderId = RAnswer.is(item) ? item.folderId : item.parentFolderId;

      if (parentFolderIdsToIgnore.has(parentFolderId || 0)) {
        return 0;
      }

      return parentFolderId || 0;
    })
    .mapValues((items) => items.sort(sortItems))
    .value();

  return _(itemsByParent[0])
    .map((i) => formatListItem(isFolderExpanded, itemsByParent, i))
    .map((i) => {
      if (i.isExpanded) {
        return [i.item, ...i.children];
      }

      return [i.item];
    })
    .flatten()
    .value();
}

interface ListItem {
  item: AnswerOrFolderModel;
  id: string;
  isExpanded: boolean;
  children: ReadonlyArray<AnswerOrFolderModel>;
}

function formatListItem(
  isFolderExpanded: PredicateOf<number>,
  itemsByParent: Dictionary<ReadonlyArray<AnswerOrFolderModel>>,
  item: AnswerOrFolderModel,
): ListItem {
  const isExpanded = RAnswer.is(item) ? false : isFolderExpanded(item.id);

  if (RAnswer.is(item)) {
    return {item, id: makeKeyForAnswerOrFolder(item), isExpanded, children: []};
  }

  const children = _(itemsByParent[item.id])
    .map((i) => formatListItem(isFolderExpanded, itemsByParent, i))
    .map((i) => {
      if (i.isExpanded) {
        return [i.item, ...i.children];
      }

      return [i.item];
    })
    .flatten()
    .value();

  return {
    id: makeKeyForAnswerOrFolder(item),
    item,
    isExpanded,
    children,
  };
}
